import * as Api from '@ViewModels';
import { action, computed, makeObservable, observable, override } from 'mobx';
import moment from 'moment';
import { orderBy, orderByDescending } from '../../models/UiUtils';
import { CompanyFilterCriteriaProperty, FilterOperator, ICompanyFilter, IFilterRequestBase } from './companyfilter';

export interface IValueRange<T> {
	min?: T;
	max?: T;
}

export enum OwnershipEnforcement {
	LeadOwner = 'LeadOwner',
	LeadVertical = 'LeadVertical',
}

export enum TurnItOnForOptions {
	Everyone = 'Everyone',
	Individual = 'Individual',
	Vertical = 'Vertical',
	DailyActivityTeam = 'DailyActivityTeam',
}

export enum TurnThisOnOptions {
	RightAway = 'RightAway',
	Scheduled = 'Scheduled',
}

export enum RecurrenceType {
	AlwaysOn = 'AlwaysOn',
	SingleInterval = 'SingleInterval',
	Weekly = 'Weekly',
}

export interface IInterval {
	startMinutes?: number;
	endMinutes?: number;
}

export interface IWeeklySchedule {
	intervals: IInterval[];
}

export interface ILeadRule extends Api.IBaseResourceModel {
	_type?: Api.LeadRuleType;
	dailyLimit?: number;
	dailyActivityTeams?: string[];
	extendedConstraintsEnabled?: boolean;
	filter?: ICompanyFilter;
	goldenHour?: boolean;
	hourlyLimit?: number;
	identifier?: string;
	isEnabled?: boolean;
	minDaysSinceLastInteraction?: number;
	name?: string;
	ownershipEnforcement?: OwnershipEnforcement;
	preserve?: boolean | null;
	priority?: number;
	recurrence?: RecurrenceType;
	schedule?: IValueRange<string>;
	users?: Api.IUserReference[];
	verticals?: string[];
	weeklySchedule?: IWeeklySchedule[];
	weight?: number;
}

export const getPropertyLevelFilters = <TProperty extends CompanyFilterCriteriaProperty, TFilterOperator>(
	filter: IFilterRequestBase<TProperty, TFilterOperator>
) => {
	let properties: IFilterRequestBase<TProperty, TFilterOperator>[] = [];
	if (!filter?.criteria?.length) {
		return properties;
	}

	filter.criteria.forEach(criteria => {
		if (criteria.property === CompanyFilterCriteriaProperty.Nested) {
			criteria.criteria.forEach(nestedCriteria => {
				const nested = getPropertyLevelFilters(nestedCriteria);
				properties = properties.concat(nested);
			});
		} else {
			properties.push(criteria);
		}
	});
	return properties;
};

export class LeadRuleViewModel extends Api.ViewModel implements ILeadRule {
	private leadRule: ILeadRule;

	constructor(userSession: Api.UserSessionContext, rule: ILeadRule) {
		super(userSession);

		makeObservable<LeadRuleViewModel, 'leadRule'>(this, {
			leadRule: observable,
			getLeadRule: computed,
			id: computed,
			name: computed,
			lastModifiedDate: computed,
			identifier: computed,
			isEnabled: computed,
			setEnabled: action,
			filter: computed,
			users: computed,
			setUsers: action,
			verticals: computed,
			setVerticals: action,
			preserve: computed,
			setPreserve: action,
			priority: computed,
			weight: computed,
			tag: computed,
			minDaysSinceLastInteraction: computed,
			setMinDaysSinceLastInteraction: action,
			goldenHour: computed,
			setGoldenHour: action,
			recurrence: computed,
			weeklySchedule: computed,
			schedule: computed,
			showSchedule: computed,
			isExpired: computed,
			scheduleMin: computed,
			scheduleMax: computed,
			setSchedule: action,
			setRecurrence: action,
			setWeeklySchedule: action,
			setDailyLimit: action,
			setHourlyLimit: action,
			dailyLimit: computed,
			hourlyLimit: computed,
			isActive: computed,
			ownershipEnforcement: computed,
			setOwnershipEnforcement: action,
			setName: action,
			setIdentifier: action,
			setPriority: action,
			type: computed,
			isCustomRule: computed,
			extendedConstraintsEnabled: computed,
			filterToArrays: computed,
			setFilterFromArrays: action,
			saved: computed,
			currentType: computed,
			load: override,
			save: action,
			delete: action,
			update: action,
			updateSchedule: action,
			setLeadRule: action,
		});

		this.userSession = userSession;
		this.leadRule = rule;
	}

	public get getLeadRule() {
		return this.leadRule;
	}
	public get id() {
		return this.leadRule?.id;
	}
	public get name() {
		return this.leadRule?.name;
	}
	public get lastModifiedDate() {
		return this.leadRule?.lastModifiedDate;
	}
	public get identifier() {
		return this.leadRule?.identifier;
	}
	public get isEnabled() {
		return this.leadRule?.isEnabled;
	}
	public setEnabled(enabled: boolean) {
		this.leadRule.isEnabled = enabled;
	}
	public get filter() {
		return this.leadRule?.filter;
	}
	public get users() {
		return this.leadRule?.users ?? [];
	}
	public setUsers(users: Api.IUserReference[]) {
		this.leadRule = { ...this.leadRule, users, verticals: [], dailyActivityTeams: [] };
	}

	public get verticals() {
		return this.leadRule?.verticals ?? [];
	}
	public setVerticals(verticals: string[]) {
		this.leadRule = { ...this.leadRule, users: [], verticals, dailyActivityTeams: [] };
	}

	public get dailyActivityTeams() {
		return this.leadRule?.dailyActivityTeams ?? [];
	}

	public setDailyActivityTeams(dailyActivityTeams: string[]) {
		this.leadRule = { ...this.leadRule, dailyActivityTeams, users: [], verticals: [] };
	}

	public get preserve() {
		return this.leadRule?.preserve;
	}
	public setPreserve(preserve: boolean | null) {
		this.leadRule = { ...this.leadRule, preserve };
	}

	public get priority() {
		return this.leadRule?.priority;
	}
	public get weight() {
		return this.leadRule?.weight;
	}
	public get tag() {
		return this.filter?.criteria?.find(x => x.property === CompanyFilterCriteriaProperty.Tag)?.value;
	}
	public get minDaysSinceLastInteraction() {
		return this.leadRule?.minDaysSinceLastInteraction;
	}
	public setMinDaysSinceLastInteraction(days: number) {
		this.leadRule = { ...this.leadRule, minDaysSinceLastInteraction: days };
	}

	public get goldenHour() {
		return this.leadRule?.goldenHour;
	}
	public setGoldenHour(enabled: boolean) {
		this.leadRule = { ...this.leadRule, goldenHour: enabled };
	}

	public get recurrence() {
		return this.leadRule?.recurrence;
	}
	public get weeklySchedule() {
		return this.leadRule?.weeklySchedule;
	}

	public get schedule() {
		return this.leadRule?.schedule;
	}

	public get showSchedule() {
		return this.leadRule?.schedule && this.leadRule?.schedule?.max;
	}
	public get isExpired() {
		return this.leadRule?.schedule && moment() > moment(this.leadRule?.schedule?.max);
	}
	public get scheduleMin() {
		const min = moment(this.leadRule?.schedule?.min);
		const max = moment(this.leadRule?.schedule?.max);
		const isSameDay = min.isSame(max, 'day');
		return isSameDay ? min.format('h:mm a') : min.format('MMM D, h:mm a');
	}
	public get scheduleMax() {
		const min = moment(this.leadRule?.schedule?.min);
		const max = moment(this.leadRule?.schedule?.max);
		const isSameDay = min.isSame(max, 'day');
		return isSameDay ? max.format('h:mm a') : max.format('MMM D, h:mm a');
	}
	public setSchedule(duration: { value: number; unit: 'hours' | 'days' } | number | null) {
		if (!duration) {
			this.leadRule.schedule = null;
			return;
		}
		// Support backward compatibility with number (hours)
		this.leadRule.schedule =
			typeof duration === 'number'
				? {
						max: moment().add(duration, 'hours').toISOString(),
						min: moment().toISOString(),
					}
				: {
						max: moment().add(duration.value, duration.unit).toISOString(),
						min: moment().toISOString(),
					};
	}

	public setScheduleByDate(endDate: Date | null) {
		if (!endDate) {
			this.leadRule.schedule = null;
			return;
		}
		this.leadRule.schedule = {
			max: moment(endDate).toISOString(),
			min: moment().toISOString(),
		};
	}

	public setRecurrence(recurrence: RecurrenceType) {
		this.leadRule.recurrence = recurrence;
	}

	public setWeeklySchedule(weeklySchedule: IWeeklySchedule[]) {
		this.leadRule.weeklySchedule = weeklySchedule;
	}

	public setDailyLimit(value: number) {
		this.leadRule.dailyLimit = value;
	}

	public setHourlyLimit(value: number) {
		this.leadRule.hourlyLimit = value;
	}

	public get dailyLimit() {
		return this.leadRule?.dailyLimit;
	}

	public get hourlyLimit() {
		return this.leadRule?.hourlyLimit;
	}

	public get isActive() {
		if (!this.isEnabled) {
			return false;
		}

		if (this.schedule == null) {
			return true;
		}

		const now = new Date();

		return new Date(this.schedule.min) < now && now < new Date(this.schedule.max);
	}

	public get ownershipEnforcement() {
		return this.leadRule?.ownershipEnforcement;
	}
	public setOwnershipEnforcement(enforcement: OwnershipEnforcement) {
		this.leadRule.ownershipEnforcement = enforcement;
	}

	public setName = (name: string) => {
		this.leadRule.name = name;
	};

	public setIdentifier = (value: string) => {
		this.leadRule.identifier = value;
	};

	public setPriority = (priority: number) => {
		this.leadRule.priority = priority;
	};

	public get type() {
		return this.leadRule?._type;
	}

	public get isCustomRule() {
		const nested = getPropertyLevelFilters(this.filter);
		if (nested.some(x => x.property !== CompanyFilterCriteriaProperty.Tag)) {
			return true;
		}
		return false;
	}

	public get extendedConstraintsEnabled() {
		return this.leadRule?.extendedConstraintsEnabled;
	}

	public get filterToArrays(): {
		and: string[];
		or: string[];
		not: string[];
		outcomes: string[];
	} {
		const filter = this.filter?.criteria;

		const and =
			filter
				?.find(x => {
					return (
						x.op === FilterOperator.And &&
						x.criteria?.[0].property !== CompanyFilterCriteriaProperty.LastInteractionType
					);
				})
				?.criteria?.map(x => x.value) ?? [];

		const or =
			filter
				?.find(x => {
					return (
						x.op === FilterOperator.Or && x.criteria?.[0].property !== CompanyFilterCriteriaProperty.LastInteractionType
					);
				})
				?.criteria?.map(x => x.value) ?? [];

		// The NOTs must be nested inside an OR array to behave intuitively
		const not =
			filter
				?.find(x => {
					return (
						x.op === FilterOperator.Not &&
						x.criteria?.[0].property !== CompanyFilterCriteriaProperty.LastInteractionType
					);
				})
				?.criteria?.[0]?.criteria?.map(x => x.value) ?? [];

		const outcomesList = filter?.find(
			x => x.criteria?.[0].property === CompanyFilterCriteriaProperty.LastInteractionType
		);
		const outcomes = outcomesList?.criteria?.map(x => x.value) || [];

		return { and, not, or, outcomes };
	}

	public setFilterFromArrays = (and: string[], or: string[], not: string[], outcomes: string[]) => {
		const filter: ICompanyFilter = {
			criteria: [
				{
					criteria: or.map(x => ({
						property: CompanyFilterCriteriaProperty.Tag,
						value: x,
					})),
					op: FilterOperator.Or,
					property: CompanyFilterCriteriaProperty.Nested,
				},
				{
					criteria: and.map(x => ({
						property: CompanyFilterCriteriaProperty.Tag,
						value: x,
					})),
					op: FilterOperator.And,
					property: CompanyFilterCriteriaProperty.Nested,
				},

				not.length
					? {
							criteria: [
								{
									criteria: not.map(x => ({
										property: CompanyFilterCriteriaProperty.Tag,
										value: x,
									})),
									op: FilterOperator.Or,
									property: CompanyFilterCriteriaProperty.Nested,
								},
							],
							op: FilterOperator.Not,
							property: CompanyFilterCriteriaProperty.Nested,
						}
					: undefined,
				{
					criteria: outcomes.map(x => ({
						property: CompanyFilterCriteriaProperty.LastInteractionType,
						value: x,
					})),
					op: FilterOperator.Or,
					property: CompanyFilterCriteriaProperty.Nested,
				},
			]
				.filter(x => !!x)

				.filter(x => x.criteria.length),
			op: FilterOperator.And,
			property: CompanyFilterCriteriaProperty.Nested,
		};
		this.leadRule = { ...this.leadRule, filter };
		return filter;
	};

	public get saved() {
		return !!this.leadRule?.id;
	}
	public get currentType(): TurnItOnForOptions {
		return this.users.length
			? TurnItOnForOptions.Individual
			: this.verticals.length
				? TurnItOnForOptions.Vertical
				: this.dailyActivityTeams.length
					? TurnItOnForOptions.DailyActivityTeam
					: TurnItOnForOptions.Everyone;
	}

	public load = async () => {
		if (!this.id || this.isBusy) {
			return false;
		}
		this.loading = true;
		const rule = await this.userSession.webServiceHelper.callWebServiceAsync<ILeadRule>(
			this.composeApiUrl({ urlPath: `aida/rule/${this.id}` }),
			'GET'
		);
		if (rule.success) {
			this.leadRule = rule.value;
		}
		this.loading = false;
		this.loaded = true;
		if (!rule.success) {
			throw Api.asApiError(rule);
		}

		return rule;
	};

	public save = async () => {
		if (this.isBusy) {
			return;
		}
		this.busy = true;
		const savedRule = await this.userSession.webServiceHelper.callWebServiceAsync<ILeadRule>(
			this.composeApiUrl({ urlPath: 'aida/rule/v2' }),
			'POST',
			this.leadRule
		);
		if (savedRule.success) {
			this.leadRule = savedRule.value;
		}
		this.busy = false;
		if (!savedRule.success) {
			throw Api.asApiError(savedRule);
		}

		return savedRule;
	};

	public delete = async () => {
		if (!this.id || this.isBusy) {
			return false;
		}
		this.busy = true;
		const deletedRule = await this.userSession.webServiceHelper.callWebServiceAsync(
			this.composeApiUrl({ urlPath: `aida/rule/${this.id}` }),
			'DELETE'
		);
		if (deletedRule.success) {
			this.leadRule = null;
		}
		this.busy = false;

		if (!deletedRule.success) {
			throw Api.asApiError(deletedRule);
		}

		return deletedRule;
	};

	public update = async () => {
		if (!this.id || this.isBusy) {
			return false;
		}
		this.busy = true;
		const updatedRule = await this.userSession.webServiceHelper.callWebServiceAsync<ILeadRule>(
			this.composeApiUrl({ urlPath: `aida/rule/${this.id}/v2` }),
			'PUT',
			this.leadRule
		);
		if (updatedRule.success) {
			this.leadRule = updatedRule.value;
		}
		this.busy = false;

		if (!updatedRule.success) {
			throw Api.asApiError(updatedRule);
		}

		return updatedRule;
	};

	public updateSchedule = async () => {
		if (!this.id || this.isBusy) {
			return false;
		}
		this.busy = true;
		const updatedRule = await this.userSession.webServiceHelper.callWebServiceAsync<ILeadRule>(
			this.composeApiUrl({ urlPath: `aida/rule/${this.id}/schedule` }),
			'PUT',
			{
				recurrence: this.leadRule.recurrence,
				schedule: this.leadRule.schedule,
				weeklySchedule: this.leadRule.weeklySchedule,
			}
		);
		if (updatedRule.success) {
			this.leadRule = updatedRule.value;
		}
		this.busy = false;

		if (!updatedRule.success) {
			throw Api.asApiError(updatedRule);
		}

		return updatedRule;
	};

	public setLeadRule = (leadRule: ILeadRule) => {
		this.leadRule = leadRule;
	};
}

/**
 * The lead rule view models represent the rules (aka blitzes, filters) which define the order in which lead searches
 * are executed. These view models are used to manage the properties, but the evaluation of rules is performed on the
 * server.
 */
export class LeadRulesViewModel extends Api.ViewModel {
	public rules: Api.BaseObservablePageCollectionController<ILeadRule, LeadRuleViewModel>;
	constructor(userSession: Api.UserSessionContext) {
		super(userSession);

		makeObservable(this, {
			rules: observable,
			reset: action,
			sort: action,
			load: override,
		});

		this.rules = new Api.BaseObservablePageCollectionController<ILeadRule, LeadRuleViewModel>({
			apiPath: this.composeApiUrl({ urlPath: 'aida/rule' }),
			client: this.userSession.webServiceHelper,
			httpMethod: 'GET',
			transformer: x => new LeadRuleViewModel(this.userSession, x),
		});
	}

	public reset = () => {
		this.rules.reset();
	};

	/**
	 * The sort logic is copied _exactly_ from the server. The paging is not normal because the sort has to be performed
	 * on properties that are not indexed on the server. In the case of removing or editing a rule, this sort can be
	 * performed rather than reloading the entire list from the server
	 */
	public sort = () => {
		this.rules.fetchResults.sort(
			(a, b) =>
				orderByDescending(a.isActive, b.isActive) ||
				orderByDescending(a.isEnabled, b.isEnabled) ||
				orderBy(a.priority, b.priority) ||
				orderBy(a.name, b.name)
		);
	};

	public load = async () => {
		this.loading = true;
		try {
			await this.rules.getNext(null, 800);
		} catch (error) {
			throw Api.asApiError(error);
		}
		this.loading = false;
		this.loaded = true;
		return this.rules;
	};
}
